import guestAxios from "../helpers/guestAxios";

import instance from "../helpers/axios";
import { instance as rawAxios } from "../hooks/axios";

import {
  ORDER_EVENT_BOOKINGS,
  ORDER_GUESTLIST,
  ORDER_GUESTLIST_BULK,
  ORDER_GUESTLIST_TEMPLATE,
  ORDER_OFFLINE_SALE,
  ORDER_VIEW_TICKETS_GUEST,
  ORDER_VIEW_TICKETS_WITH_TICKET_ID,
} from "./endPoint";
import { formatAxiosErrorMessage } from "./httpError";
import {
  assertBlobIsNotJsonError,
  buildGuestlistTemplateFilename,
  coerceDownloadBlob,
  parseContentDispositionFilename,
} from "../helpers/fileDownload";
import {
  applyAuthorizationHeader,
  buildAuthorizationHeader,
  readStoredAccessToken,
} from "../helpers/authToken";



/** POST /order/offlineSale — Auth: ADMIN | CLUB_MANAGER | CLUB_EMPLOYEE */

export const postOfflineSale = (body) =>

  instance.post(ORDER_OFFLINE_SALE, body);



/** POST /order/guestlist — Auth: ADMIN | CLUB_MANAGER (403 for CLUB_EMPLOYEE) */

export const postGuestlist = (body) => instance.post(ORDER_GUESTLIST, body);

function orderApiAuthHeader() {
  const authorization = buildAuthorizationHeader(readStoredAccessToken());
  return authorization ? { Authorization: authorization } : {};
}

function buildGuestlistTemplateRequestConfig(format = "xlsx") {
  const accept =
    format === "csv"
      ? "text/csv, application/octet-stream, */*"
      : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/octet-stream, */*";
  const config = {
    responseType: "blob",
    withCredentials: true,
    headers: {
      Accept: accept,
    },
    params: format === "csv" ? { format: "csv" } : undefined,
  };
  const authorization = buildAuthorizationHeader(readStoredAccessToken());
  if (authorization) {
    applyAuthorizationHeader(config, authorization);
  }
  return config;
}

function assertGuestlistTemplatePayload(data, status) {
  if (data == null || data === "") {
    const code = status != null ? ` (HTTP ${status})` : "";
    throw new Error(
      `Template download returned no file${code}. The server may not have generated the Excel file yet — confirm GET /order/guestlist/template/:eventId returns an .xlsx body.`
    );
  }
  if (data instanceof Blob && data.size === 0) {
    throw new Error(
      "Template file is empty (0 bytes). The backend template endpoint responded but did not send Excel data."
    );
  }
}

async function parseBlobErrorPayload(data) {
  if (!(data instanceof Blob)) return data;
  try {
    const text = await data.text();
    return JSON.parse(text);
  } catch {
    return data;
  }
}

async function throwOrderApiError(error) {
  const status = error?.response?.status;
  let data = error?.response?.data;
  if (data instanceof Blob) {
    data = await parseBlobErrorPayload(data);
    if (error?.response) {
      error.response.data = data;
    }
  }
  const err = new Error(formatAxiosErrorMessage(error));
  err.status = status;
  throw err;
}

/**
 * GET /order/guestlist/template/:eventId — .xlsx or .csv blob download.
 * Auth: CLUB_MANAGER (Bearer).
 * @param {string} eventId
 * @param {{ eventTitle?: string, format?: "xlsx" | "csv" }} [options]
 * @returns {Promise<{ blob: Blob, filename: string }>}
 */
export async function downloadGuestlistTemplate(eventId, { eventTitle, format = "xlsx" } = {}) {
  const id = String(eventId ?? "").trim();
  if (!id) {
    throw new Error("Event id is required");
  }
  const templateFormat = format === "csv" ? "csv" : "xlsx";

  try {
    const response = await rawAxios.get(
      ORDER_GUESTLIST_TEMPLATE(id),
      buildGuestlistTemplateRequestConfig(templateFormat)
    );
    const status = response?.status;
    const contentType = response.headers?.["content-type"];
    assertGuestlistTemplatePayload(response.data, status);
    let blob = coerceDownloadBlob(response.data, contentType);
    if (blob.size === 0) {
      throw new Error(
        "Template file is empty (0 bytes). The backend template endpoint responded but did not send Excel data."
      );
    }
    blob = await assertBlobIsNotJsonError(blob, contentType);
    const serverFilename = parseContentDispositionFilename(
      response.headers?.["content-disposition"]
    );
    const title = String(eventTitle ?? "").trim();
    const filename = title
      ? buildGuestlistTemplateFilename(title, templateFormat)
      : serverFilename ||
        `guestlist-template.${templateFormat === "csv" ? "csv" : "xlsx"}`;
    return { blob, filename };
  } catch (error) {
    let data = error?.response?.data;
    data = await parseBlobErrorPayload(data);
    if (error?.response) {
      error.response.data = data;
    }
    await throwOrderApiError(error);
  }
}

/**
 * Drop default application/json Content-Type so the browser can set
 * multipart/form-data with the correct boundary for FormData bodies.
 * @param {import("axios").AxiosRequestHeaders | Record<string, unknown>} headers
 * @param {unknown} data
 */
function unsetJsonContentTypeForFormData(headers, data) {
  if (!(data instanceof FormData) || !headers) return data;
  if (typeof headers.delete === "function") {
    headers.delete("Content-Type");
    headers.delete("content-type");
  } else {
    delete headers["Content-Type"];
    delete headers["content-type"];
  }
  return data;
}

/**
 * POST /order/guestlist/bulk — multipart FormData (eventid + file).
 * Do not JSON-encode the File; bytes must travel as multipart.
 * Auth: CLUB_MANAGER | ADMIN (Bearer).
 * @param {string|FormData} eventIdOrFormData
 * @param {File|Blob} [file]
 * @returns {Promise<{ message?: string, data?: object }>}
 */
export async function postGuestlistBulk(eventIdOrFormData, file) {
  let formData;
  if (eventIdOrFormData instanceof FormData) {
    formData = eventIdOrFormData;
  } else {
    const eventId = String(eventIdOrFormData ?? "").trim();
    if (!eventId) {
      throw new Error("postGuestlistBulk requires eventid");
    }
    if (!(file instanceof Blob)) {
      throw new Error("postGuestlistBulk requires a File");
    }
    formData = new FormData();
    formData.append("eventid", eventId);
    formData.append("file", file);
  }

  try {
    const response = await rawAxios.post(ORDER_GUESTLIST_BULK, formData, {
      headers: {
        ...orderApiAuthHeader(),
        // Override instance default application/json; transformRequest deletes it
        // so the browser sets multipart/form-data + boundary.
        "Content-Type": false,
      },
      transformRequest: [
        (data, headers) => unsetJsonContentTypeForFormData(headers, data),
      ],
      withCredentials: true,
    });
    const body = response?.data;
    if (body && typeof body === "object" && body.data != null) {
      return body;
    }
    return { data: body, message: body?.message };
  } catch (error) {
    await throwOrderApiError(error);
  }
}



/** Mongo ObjectId string (24 hex chars). */

export function isMongoOrderId(orderId) {

  return (

    typeof orderId === "string" &&

    /^[a-f\d]{24}$/i.test(String(orderId).trim())

  );

}



/** Guest share token: trim + lowercase 64 hex (SMS/email links). */

export function normalizeGuestHexToken(token) {

  if (token == null || token === "") return "";

  return String(token).trim().toLowerCase();

}



export function isGuestHexToken64(token) {

  const t = normalizeGuestHexToken(token);

  return /^[a-f0-9]{64}$/.test(t);

}



/** Redact middle of token for dev logs only. */

export function redactGuestTokenForLog(token) {

  const t = normalizeGuestHexToken(token);

  if (!t) return "(empty)";

  if (t.length <= 16) return `(len=${t.length})`;

  return `${t.slice(0, 6)}…${t.slice(-4)} (len=${t.length})`;

}



/**

 * Guest offline ticket link. No Authorization header.

 * GET /order/viewTicketsGuest/:orderId?token=<64 hex>

 * Uses one query param `token` via URLSearchParams (no duplicate access_token).

 */

export function fetchOrderTicketsGuest(orderId, token) {

  const id = String(orderId ?? "").trim();

  const tok = normalizeGuestHexToken(token);



  if (!isMongoOrderId(id)) {

    const err = new Error("INVALID_ORDER_ID");

    err.code = "INVALID_ORDER_ID";

    err.status = 400;

    return Promise.reject(err);

  }

  if (!isGuestHexToken64(tok)) {

    const err = new Error("INVALID_TOKEN");

    err.code = "INVALID_TOKEN";

    err.status = 400;

    return Promise.reject(err);

  }



  const params = new URLSearchParams();

  params.set("token", tok);



  const path = `${ORDER_VIEW_TICKETS_GUEST}${id}?${params.toString()}`;



  if (process.env.NODE_ENV === "development") {

    /* eslint-disable no-console */

    console.debug(

      "[viewTicketsGuest] GET",

      `${ORDER_VIEW_TICKETS_GUEST}${id}?token=${redactGuestTokenForLog(tok)}`

    );

    if (tok.length !== 64) {

      console.warn("[viewTicketsGuest] token length !== 64:", tok.length);

    }

    /* eslint-enable no-console */

  }



  return guestAxios.get(path);

}



/**

 * Logged-in buyer (or app token). Uses default instance + Bearer from localStorage.

 * GET /order/viewTicketsWithTicketId/:orderId

 */

export const fetchOrderTicketsWithTicketId = (orderId) =>

  instance.get(`${ORDER_VIEW_TICKETS_WITH_TICKET_ID}${orderId}`);



/**
 * Admin event-wise bookings (PII-masked buyer fields).
 * GET /order/event/:eventId/bookings?status=<status>&page=<n>&limit=<n>
 * Auth: admin bearer token (sent automatically by the default axios instance).
 * @param {string} eventId Mongo id of the selected event.
 * @param {{ status?: string, page?: number, limit?: number }} [opts]
 */
export const fetchEventBookings = (eventId, opts = {}) => {

  const id = String(eventId ?? "").trim();

  const { status, page, limit } = opts;

  const params = {};

  if (status != null && String(status).trim() !== "") {
    params.status = String(status).trim();
  }
  if (page != null && Number(page) > 0) params.page = Number(page);
  if (limit != null && Number(limit) > 0) params.limit = Number(limit);

  return instance.get(ORDER_EVENT_BOOKINGS(id), { params });
};

